"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLegendItems = exports.shouldLegendBeEnabled = void 0;
var tslib_1 = require("tslib");
// (C) 2007-2021 GoodData Corporation
var pick_1 = tslib_1.__importDefault(require("lodash/pick"));
var set_1 = tslib_1.__importDefault(require("lodash/set"));
var common_1 = require("../chartTypes/_util/common");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var legendHelpers_1 = require("./legendHelpers");
var chartCapabilities_1 = require("../chartTypes/_chartOptions/chartCapabilities");
var sdk_ui_vis_commons_1 = require("@gooddata/sdk-ui-vis-commons");
function isHeatmapWithMultipleValues(chartOptions) {
    var _a, _b;
    var type = chartOptions.type;
    var dataClasses = (_b = (_a = chartOptions === null || chartOptions === void 0 ? void 0 : chartOptions.colorAxis) === null || _a === void 0 ? void 0 : _a.dataClasses) !== null && _b !== void 0 ? _b : [];
    return common_1.isHeatmap(type) && dataClasses.length > 1;
}
function shouldLegendBeEnabled(chartOptions) {
    var _a, _b;
    var seriesLength = (_b = (_a = chartOptions === null || chartOptions === void 0 ? void 0 : chartOptions.data) === null || _a === void 0 ? void 0 : _a.series) === null || _b === void 0 ? void 0 : _b.length;
    var type = chartOptions.type, hasStackByAttribute = chartOptions.hasStackByAttribute, hasViewByAttribute = chartOptions.hasViewByAttribute;
    var hasMoreThanOneSeries = seriesLength > 1;
    var isLineChartStacked = common_1.isLineChart(type) && hasStackByAttribute;
    var isStacked = legendHelpers_1.isStackedChart(chartOptions);
    var sliceTypes = [sdk_ui_1.VisualizationTypes.PIE, sdk_ui_1.VisualizationTypes.DONUT];
    var isSliceChartWithViewByAttributeOrMultipleMeasures = common_1.isOneOfTypes(type, sliceTypes) && (hasViewByAttribute || chartOptions.data.series[0].data.length > 1);
    var isBubbleWithViewByAttribute = common_1.isBubbleChart(type) && hasViewByAttribute;
    var isScatterPlotWithAttribute = common_1.isScatterPlot(type) && chartOptions.data.series[0].name;
    var isTreemapWithViewByAttribute = common_1.isTreemap(type) && hasViewByAttribute;
    var isTreemapWithManyCategories = common_1.isTreemap(type) && chartOptions.data.categories.length > 1;
    return (hasMoreThanOneSeries ||
        isSliceChartWithViewByAttributeOrMultipleMeasures ||
        isStacked ||
        isLineChartStacked ||
        isScatterPlotWithAttribute ||
        isTreemapWithViewByAttribute ||
        isBubbleWithViewByAttribute ||
        isTreemapWithManyCategories ||
        isHeatmapWithMultipleValues(chartOptions));
}
exports.shouldLegendBeEnabled = shouldLegendBeEnabled;
function getLegendItems(chartOptions) {
    var _a, _b;
    var type = chartOptions.type;
    var firstSeriesDataTypes = [
        sdk_ui_1.VisualizationTypes.PIE,
        sdk_ui_1.VisualizationTypes.DONUT,
        sdk_ui_1.VisualizationTypes.TREEMAP,
        sdk_ui_1.VisualizationTypes.FUNNEL,
        sdk_ui_1.VisualizationTypes.SCATTER,
    ];
    if (common_1.isHeatmap(type)) {
        var dataClasses = (_b = (_a = chartOptions === null || chartOptions === void 0 ? void 0 : chartOptions.colorAxis) === null || _a === void 0 ? void 0 : _a.dataClasses) !== null && _b !== void 0 ? _b : [];
        return dataClasses.map(function (dataClass, index) {
            var from = dataClass.from, to = dataClass.to;
            var color = dataClass.color; // wa are not using Gradient
            var range = {
                from: from,
                to: to,
            };
            return {
                range: range,
                color: color,
                legendIndex: index,
            };
        });
    }
    var legendDataSource = common_1.isOneOfTypes(type, firstSeriesDataTypes)
        ? chartOptions.data.series[0].data
        : chartOptions.data.series;
    var pickedProps = ["name", "color", "legendIndex"];
    if (common_1.isOneOfTypes(type, chartCapabilities_1.supportedDualAxesChartTypes)) {
        // 'yAxis' helps to distinguish primary and secondary axes
        pickedProps = tslib_1.__spreadArrays(pickedProps, ["yAxis"]);
    }
    if (common_1.isComboChart(type)) {
        pickedProps = tslib_1.__spreadArrays(pickedProps, ["type"]);
    }
    return legendDataSource
        .filter(function (legendDataSourceItem) { return legendDataSourceItem.showInLegend !== false; })
        .map(function (legendDataSourceItem) { return pick_1.default(legendDataSourceItem, pickedProps); });
}
exports.getLegendItems = getLegendItems;
function buildLegendOptions(legendConfig, chartOptions) {
    var _a, _b;
    if (legendConfig === void 0) { legendConfig = {}; }
    var defaultLegendConfigByType = {};
    var rightLegendCharts = [
        sdk_ui_1.VisualizationTypes.SCATTER,
        sdk_ui_1.VisualizationTypes.TREEMAP,
        sdk_ui_1.VisualizationTypes.BUBBLE,
        sdk_ui_1.VisualizationTypes.HEATMAP,
    ];
    var defaultTopLegendCharts = [
        sdk_ui_1.VisualizationTypes.COLUMN,
        sdk_ui_1.VisualizationTypes.BAR,
        sdk_ui_1.VisualizationTypes.BULLET,
        sdk_ui_1.VisualizationTypes.LINE,
        sdk_ui_1.VisualizationTypes.AREA,
        sdk_ui_1.VisualizationTypes.PIE,
        sdk_ui_1.VisualizationTypes.DONUT,
    ];
    if (legendConfig.position === "auto" || !legendConfig.position) {
        if (common_1.isOneOfTypes(chartOptions.type, rightLegendCharts)) {
            set_1.default(defaultLegendConfigByType, "position", "right");
        }
        if (common_1.isOneOfTypes(chartOptions.type, defaultTopLegendCharts) && !chartOptions.hasStackByAttribute) {
            set_1.default(defaultLegendConfigByType, "position", "top");
        }
    }
    var baseConfig = tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, sdk_ui_vis_commons_1.DEFAULT_LEGEND_CONFIG), legendConfig), defaultLegendConfigByType);
    var isLegendEnabled = shouldLegendBeEnabled(chartOptions);
    return tslib_1.__assign(tslib_1.__assign({}, baseConfig), { enabled: baseConfig.enabled && isLegendEnabled, toggleEnabled: isLegendEnabled, format: (_b = (_a = chartOptions === null || chartOptions === void 0 ? void 0 : chartOptions.title) === null || _a === void 0 ? void 0 : _a.format) !== null && _b !== void 0 ? _b : "", items: getLegendItems(chartOptions), enableBorderRadius: createItemBorderRadiusPredicate(chartOptions.type), seriesMapper: createSeriesMapper(chartOptions.type) });
}
exports.default = buildLegendOptions;
/**
 * Given chart type, this creates predicate to turn legend item border radius on or off. The border
 * radius is set to make legend item indicators appear as circles instead of squares.
 *
 * The predicate is crafted so that line and area charts have indicators as circles. This also stands for
 * combo chart - line or area items within combo must use circles.
 *
 * @param chartType - top-level chart type (combo chart will have legend items of different types)
 */
function createItemBorderRadiusPredicate(chartType) {
    if (common_1.isLineChart(chartType) || common_1.isAreaChart(chartType)) {
        /*
         * It is clear that all items are of same type and should have indicators as circles.
         */
        return true;
    }
    else if (common_1.isComboChart(chartType)) {
        /*
         * For combo chart, determine item-by-item
         */
        return function (item) {
            return common_1.isLineChart(item.type) || common_1.isAreaChart(item.type);
        };
    }
    return false;
}
/**
 * Given chart type, this function creates a series mapper which will alter previously created legend
 * items. This code was previously insight the legend implementation. It is extracted here to make legends
 * visualization agnostic.
 *
 * The big question is - why is this mapper even needed, why not creating the legend items correctly the first time?
 * It is likely that the code was glued-in while implementing combo and dual axes charts. Perhaps better way is to
 * refactor the legend builders (which can be chart-specific) to create the legend items correctly.
 */
function createSeriesMapper(chartType) {
    if (common_1.isComboChart(chartType)) {
        return legendHelpers_1.getComboChartSeries;
    }
    return legendHelpers_1.createDualAxesSeriesMapper(chartType);
}
//# sourceMappingURL=legendBuilder.js.map